'use client';

import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paragraph } from '../ui/paragraph';
import CategorySort from '@eliadmin/core/src/components/categories/category-sort';
import { CategoryFilterSheet } from '@eliadmin/core/src/components/categories/category-filter-sheet';
import { CategoryFilterLeft } from '@eliadmin/core/src/components/categories/category-filter-left';
import { ProductCard } from '../product/product-card';
import { CategoryPagination } from './category-pagination';
import {
  buildCategoryProductsQuery,
  categoryProductsKey,
  matchingCategorySeed,
  type CategoryProductsSeed,
} from '@/lib/category-products-query';
import { Button } from '../ui/button';
import { Modal } from '../ui/modal';
import { ButtonIcon } from '../ui/button-icon';
import { CategoryFilterContent } from './category-filter-content';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useCategoryFilterContext } from './lazy-category-provider';

export type CategoryProps = {
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
  locale: string;
  initialProducts?: CategoryProductsSeed;
};

function CategoryProductPlaceholders() {
  return Array.from({ length: 6 }, (_, index) => (
    <div
      key={index}
      className="flex min-h-[360px] flex-col gap-3 rounded-[12px] bg-[#F9FAFB] p-3 md:min-h-[420px] md:gap-4 md:p-4"
    >
      <Skeleton className="aspect-square w-full rounded-[12px]" />
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-4/5" />
      <Skeleton className="h-7 w-2/5" />
      <Skeleton className="h-8 w-full" />
    </div>
  ));
}

export function CategoryContentSkeleton() {
  return (
    <section
      className={cn('custom-section', 'gap-6 pt-6 sm:gap-8 sm:pt-8 md:pt-10 lg:pt-12')}
      aria-busy="true"
      aria-label="Načítavajú sa produkty"
    >
      <div
        className={cn(
          'custom-container',
          'flex w-full flex-col-reverse items-center justify-center gap-6 md:flex-row md:justify-between',
        )}
      >
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-10 w-full md:w-44" />
      </div>
      <div className={cn('custom-container', 'flex items-start justify-between gap-4 lg:gap-6')}>
        <div className="hidden w-full max-w-[332px] space-y-3 md:block">
          <Skeleton className="h-12 w-full rounded-[12px]" />
          <Skeleton className="h-48 w-full rounded-[12px]" />
          <Skeleton className="h-36 w-full rounded-[12px]" />
        </div>
        <div className="grid w-full max-w-[1044px] grid-cols-2 gap-x-2 gap-y-6 lg:grid-cols-3 lg:gap-x-4 xl:gap-x-6">
          <CategoryProductPlaceholders />
        </div>
      </div>
    </section>
  );
}

export function CategoryContent(props: CategoryProps) {
  const { load: loadFilterContext } = useCategoryFilterContext();
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const filters = useCategoryStore(s => s.filter);
  const currentPage = useCategoryStore(s => s.page);
  const paramsReady = useCategoryStore(s => s.paramsReady);
  const queryClient = useQueryClient();

  const { t } = useTranslations();

  const generalLayout = useGlobalStore(s => s.generalLayout);
  const queryWithoutPage = useMemo(
    () =>
      buildCategoryProductsQuery({
        categoryId: props.category.id,
        results: generalLayout?.pagination?.paginationProducts || 12,
        filter: filters,
      }),
    [props.category.id, generalLayout?.pagination?.paginationProducts, filters],
  );

  const queryKey = categoryProductsKey({
    query: queryWithoutPage,
    page: currentPage,
    locale: props.locale,
    authToken,
  });
  const seed = matchingCategorySeed(props.initialProducts, queryKey);

  const productsQuery = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale: props.locale,
        authToken,
        signal,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage,
    initialData: seed ? { pages: [seed.data], pageParams: [currentPage] } : undefined,
    initialDataUpdatedAt: seed?.updatedAt,
    enabled: isInitialized && paramsReady,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];
  const [loadMode, setLoadMode] = useState<'manual' | 'auto'>('manual');
  const [fetchTrigger, setFetchTrigger] = useState<'load-more' | 'show-all' | 'auto' | null>(null);
  const autoLoadRef = useRef<HTMLDivElement | null>(null);

  const fetchNextPageSafely = useCallback(
    async (trigger: 'load-more' | 'show-all' | 'auto') => {
      // Manual fetchNextPage ignores React Query's enabled flag.
      if (!isInitialized || !paramsReady) return false;
      if (!productsQuery.hasNextPage) return false;
      if (productsQuery.isFetchingNextPage) return false;
      setFetchTrigger(trigger);
      try {
        await productsQuery.fetchNextPage();
        return true;
      } finally {
        setFetchTrigger(null);
      }
    },
    [productsQuery, isInitialized, paramsReady],
  );

  useEffect(() => {
    if (loadMode !== 'auto') return;
    if (!productsQuery.hasNextPage) return;

    const target = autoLoadRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        void fetchNextPageSafely('auto');
      },
      { root: null, rootMargin: '250px 0px', threshold: 0 },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPageSafely, loadMode, productsQuery.hasNextPage]);

  useEffect(() => {
    setLoadMode('manual');
    setFetchTrigger(null);
  }, [currentPage, props.locale, authToken, queryWithoutPage]);

  useEffect(() => {
    const total = lastPage?.meta?.total;
    if (typeof total === 'number') props.onProductTotalChange?.(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  // Keep the filter/sort/dialog shell mounted while only this query's results change.
  // Do not use previous-query placeholders: those could show another filter/customer's prices.
  const isPending = !productsQuery.isError && (productsQuery.isPending || !productsQuery.data);
  const hasResults = !isPending && !productsQuery.isError;
  const hasProducts = flatList.length > 0;

  return (
    <>
      <section
        id="category-products"
        className={cn(
          'custom-section',
          'scroll-mt-24 gap-6 pt-6 sm:gap-8 sm:pt-8 md:pt-10 lg:pt-12',
        )}
      >
        <div
          className={cn(
            'custom-container',
            'flex w-full flex-col-reverse items-center justify-center gap-6 md:flex-row md:justify-between',
          )}
        >
          <Paragraph size="sm" color="muted-foreground" className="w-full text-center sm:w-fit">
            {hasResults ? (
              <>{productsQuery.data?.pages[0]?.meta?.total} produktov</>
            ) : (
              <Skeleton className="inline-block h-5 w-24" />
            )}
          </Paragraph>
          <div className="flex w-full flex-col items-center justify-center gap-2 md:w-fit">
            <div className="flex w-full items-center justify-center md:w-fit md:justify-end">
              <CategorySort />
            </div>
            <div className="hidden w-full sm:block md:hidden">
              <CategoryFilterSheet />
            </div>
          </div>
        </div>
        <div className={cn('custom-container', 'flex items-start justify-between gap-4 lg:gap-6')}>
          <div className="hidden w-full max-w-[332px] md:block">
            <CategoryFilterLeft />
          </div>

          <div
            className="flex w-full max-w-[1044px] flex-col items-center justify-center gap-6"
            aria-busy={isPending}
            aria-label={isPending ? 'Načítavajú sa produkty' : undefined}
          >
            <div className="grid w-full grid-cols-2 gap-x-2 gap-y-6 lg:grid-cols-3 lg:gap-x-4 xl:gap-x-6">
              {isPending ? (
                <CategoryProductPlaceholders />
              ) : productsQuery.isError ? (
                <div
                  role="alert"
                  className="col-span-full flex flex-col items-center gap-4 py-12 text-center"
                >
                  <Paragraph size="lg" color="muted-foreground">
                    Produkty sa nepodarilo načítať.
                  </Paragraph>
                  <Button
                    variant="outline"
                    disabled={!isInitialized || !paramsReady}
                    onClick={() => void productsQuery.refetch()}
                  >
                    {t('text595')}
                  </Button>
                </div>
              ) : hasProducts ? (
                flatList.map((product, index) => (
                  <ProductCard
                    headingTag="h2"
                    product={product}
                    key={product.slug}
                    imagePriority={index === 0}
                    imageSizes="(max-width: 767px) calc((100vw - 72px) / 2), (max-width: 1023px) 33vw, (max-width: 1279px) 25vw, (max-width: 1439px) 23vw, 300px"
                  />
                ))
              ) : (
                <Paragraph
                  size="lg"
                  color="muted-foreground"
                  className="col-span-full mb-4 mt-12 text-center"
                >
                  {t('text3687')}
                </Paragraph>
              )}
            </div>
            {hasResults && loadMode === 'manual' && !!productsQuery.hasNextPage && (
              <div
                className={cn(
                  'flex flex-col justify-center gap-2 md:flex-row',
                  'ea-category-products__load-more',
                )}
              >
                <Button
                  variant="outline"
                  disabled={!isInitialized || !paramsReady}
                  isLoading={productsQuery.isFetchingNextPage && fetchTrigger === 'load-more'}
                  onClick={() => void fetchNextPageSafely('load-more')}
                >
                  {t('text338')}
                </Button>
              </div>
            )}

            {hasResults && loadMode === 'auto' && !!productsQuery.hasNextPage && (
              <div
                className={cn(
                  'flex flex-col items-center justify-center gap-2',
                  'ea-category-products__auto-load',
                )}
              >
                <div ref={autoLoadRef} className="h-1 w-full" />
                {!!productsQuery.isFetchingNextPage && (
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
                )}
              </div>
            )}

            <div className={cn('flex flex-row justify-center', 'ea-category-products__pagination')}>
              <CategoryPagination
                links={hasResults ? lastPage?.meta.links : undefined}
                onPageClicked={page => {
                  if (String(page) !== String(currentPage)) return;
                  // Collapse appended pages without refetching the already cached first page.
                  queryClient.setQueryData<typeof productsQuery.data>(queryKey, data => {
                    if (!data) return data;
                    return {
                      pages: data.pages.slice(0, 1),
                      pageParams: data.pageParams.slice(0, 1),
                    };
                  });
                }}
                scrollTarget={props.paginationProps?.scrollToElementAfterPageChange || undefined}
              />
            </div>
          </div>
        </div>
      </section>
      <Modal
        trigger={
          <Button
            variant={'secondary'}
            className="fixed bottom-16 right-4 z-30 flex sm:hidden"
            onClick={loadFilterContext}
          >
            <ButtonIcon alt={t('text174')} url="/images/icons/filter-black.svg" /> {t('text174')}
          </Button>
        }
        className={{ content: cn('max-h-[92dvh] rounded-t-[12px]') }}
      >
        <CategoryFilterContent />
      </Modal>
    </>
  );
}
